/**
 * Allowlist of URL prefixes the LLM may emit inside `<heidi-button>` tags.
 *
 * Mirrored on the backend in `apis/v2/chat/heidi_button_allowlist.py`
 * (ml-scribe) — keep both files in sync.
 *
 * Two layers of validation:
 * 1. Backend streaming validator strips disallowed URLs before they reach the
 *    SSE stream (defence-in-depth).
 * 2. This frontend allowlist guards against any URL that slips through (e.g.
 *    historical messages rendered before the BE validator landed) and gates
 *    relative-path resolution to known scribe-FE origins only.
 */

import { ENV_NAME, getEnvironment } from '@heidi/api/config';

/**
 * Origins where the chat may run. `window.location.origin` must match one of
 * these before we resolve any relative href against it.
 */
export const HEIDI_BUTTON_ALLOWED_HOSTS = [
  'https://scribe.heidihealth.com',
  'https://scribe.dev.heidihealth.com',
  'https://registrar.scribe.heidihealth.com',
  'http://localhost:3000',
] as const;

export type HeidiButtonDestination =
  | 'billing'
  | 'pricing'
  | 'account'
  | 'evidence'
  | 'templates'
  | 'library'
  | 'cpd'
  | 'marketing'
  | 'other';

/**
 * Settings destinations the LLM may emit, mapped to the canonical in-app
 * route for each. Single source of truth for three otherwise-parallel
 * concerns:
 *
 *  1. Allowlisting — spread into `HEIDI_BUTTON_ALLOWED_RELATIVE_PREFIXES`
 *     below so `resolveAndValidateHeidiButtonHref` accepts the href.
 *  2. Analytics — the classifier (`classifyHeidiButtonHref`) iterates
 *     this map first so each route lands under its own destination
 *     bucket in Amplitude.
 *  3. In-app navigation — `HeidiButton` imports this map to decide
 *     whether to dispatch to the settings modal / `router.push` instead
 *     of opening a new browser tab or the OS browser on Tauri.
 *
 * Adding a new entry here automatically extends all three. Keep the
 * matching backend entry in `apis/v2/chat/heidi_button_allowlist.py`
 * (ml-scribe) in sync — that mirror is across repos and can't be derived.
 */
export const HEIDI_BUTTON_SETTINGS_ROUTES = {
  billing: '/settings/billing',
  account: '/settings/account',
  evidence: '/settings/evidence',
} as const satisfies Partial<Record<HeidiButtonDestination, string>>;

type SettingsDestination = keyof typeof HEIDI_BUTTON_SETTINGS_ROUTES;

const SETTINGS_DESTINATIONS = Object.keys(
  HEIDI_BUTTON_SETTINGS_ROUTES,
) as readonly SettingsDestination[];

/**
 * Paths the LLM may emit as a relative href (resolved against current origin).
 * Settings routes come from `HEIDI_BUTTON_SETTINGS_ROUTES`; add other
 * same-origin destinations directly below.
 */
export const HEIDI_BUTTON_ALLOWED_RELATIVE_PREFIXES = [
  ...Object.values(HEIDI_BUTTON_SETTINGS_ROUTES),
  '/scribe/my-templates',
  '/library',
  '/evidence/cpd',
] as const;

/**
 * Cross-domain absolute URL prefixes. Used for marketing/pricing pages and
 * for the same-origin scribe URLs the markdown-link variant emits on mobile.
 */
export const HEIDI_BUTTON_ALLOWED_ABSOLUTE_PREFIXES = [
  'https://www.heidihealth.com/',
  'https://heidihealth.com/',
  'https://scribe.heidihealth.com/',
  'https://scribe.dev.heidihealth.com/',
  'https://registrar.scribe.heidihealth.com/',
  'http://localhost:3000/',
] as const;

/**
 * Classify a resolved absolute URL into a small enum used for analytics
 * grouping AND to drive in-app navigation in `HeidiButton`. Keeps Amplitude
 * dashboards clean while the raw `href` is still captured separately for
 * debugging.
 *
 * Path-based checks (settings / templates / library / cpd) match against the
 * URL's `pathname` only — never the full string — so a query or hash that
 * happens to contain a settings prefix (e.g. `/library?next=/settings/billing`)
 * doesn't misclassify the destination and trigger the wrong navigation
 * branch downstream. Origin-based checks (marketing, pricing) still
 * inspect the full URL since they're host-discriminating by design.
 *
 * Settings checks run first — driven by `HEIDI_BUTTON_SETTINGS_ROUTES` —
 * so the rest of the ordering only has to worry about its own overlaps
 * (e.g. `/evidence/cpd` must be matched before `/library` because both
 * share the `library` substring, etc.).
 */
export function classifyHeidiButtonHref(url: string): HeidiButtonDestination {
  let pathname = url;
  try {
    pathname = new URL(url).pathname;
  } catch {
    // Fall through: caller is expected to pass an already-resolved absolute
    // URL, but a malformed string is still safe to substring-match — the
    // worst case is a less-precise classification, which only affects the
    // analytics bucket.
  }

  for (const destination of SETTINGS_DESTINATIONS) {
    if (pathname.startsWith(HEIDI_BUTTON_SETTINGS_ROUTES[destination])) {
      return destination;
    }
  }
  if (pathname.startsWith('/scribe/my-templates')) return 'templates';
  if (pathname.startsWith('/evidence/cpd')) return 'cpd';
  if (pathname.startsWith('/library')) return 'library';
  if (url.includes('heidihealth.com/pricing')) return 'pricing';
  if (
    url.startsWith('https://www.heidihealth.com/') ||
    url.startsWith('https://heidihealth.com/')
  )
    return 'marketing';
  return 'other';
}

/**
 * Resolve the scribe-FE origin to use as the base when the LLM emits a relative
 * href like `/settings/billing`. Used by Tauri desktop, where
 * `window.location.origin` is `tauri://localhost` (not in the allowlist) and
 * therefore cannot itself anchor a real URL the OS browser can open.
 *
 * The mapping is keyed off `NEXT_PUBLIC_ENVIRONMENT` and always returns one of
 * `HEIDI_BUTTON_ALLOWED_HOSTS` so the validator downstream still passes.
 */
export function getScribeFeOriginForEnv(): string {
  switch (getEnvironment()) {
    case ENV_NAME.PROD:
      return 'https://scribe.heidihealth.com';
    case ENV_NAME.STAGING:
      return 'https://registrar.scribe.heidihealth.com';
    case ENV_NAME.LOCALHOST:
      return 'http://localhost:3000';
    case ENV_NAME.DEV:
    case ENV_NAME.AZURE_DEV:
    default:
      return 'https://scribe.dev.heidihealth.com';
  }
}

/**
 * Validate a `<heidi-button href="…">` value and resolve it to an absolute URL
 * that is safe to navigate to. Returns null when the href is not allowlisted
 * or cannot be resolved (e.g. running on a non-allowlisted host).
 */
export function resolveAndValidateHeidiButtonHref(
  href: string | undefined,
  origin: string,
): string | null {
  if (!href) return null;

  // Relative path: must match an allowlisted prefix AND we must be running on
  // an allowlisted origin before we resolve it.
  if (href.startsWith('/')) {
    const prefixOk = HEIDI_BUTTON_ALLOWED_RELATIVE_PREFIXES.some((prefix) =>
      href.startsWith(prefix),
    );
    if (!prefixOk) return null;

    const originOk = (HEIDI_BUTTON_ALLOWED_HOSTS as readonly string[]).includes(
      origin,
    );
    if (!originOk) return null;

    try {
      return new URL(href, origin).toString();
    } catch {
      return null;
    }
  }

  // Absolute URL: must match an allowlisted absolute prefix verbatim.
  if (href.startsWith('http://') || href.startsWith('https://')) {
    const prefixOk = HEIDI_BUTTON_ALLOWED_ABSOLUTE_PREFIXES.some((prefix) =>
      href.startsWith(prefix),
    );
    if (!prefixOk) return null;

    try {
      return new URL(href).toString();
    } catch {
      return null;
    }
  }

  return null;
}
